// @flow

import { Event, ErrorEvent, Evented } from "../util/evented";

import { extend } from "../util/util";
import EXTENT from "../data/extent";
import { ResourceType } from "../util/ajax";
import browser from "../util/browser";
import SourceEnhance from "./source_enhance";

import type { Source } from "./source";
import type Map from "../ui/map";
import type Dispatcher from "../util/dispatcher";
import type Tile from "./tile";
import type Actor from "../util/actor";
import type { Callback } from "../types/callback";
import type { GeoJSON, GeoJSONFeature } from "@mapbox/geojson-types";
import type {
    GeoJSONSourceSpecification,
    PromoteIdSpecification,
} from "../style-spec/types";

import { loopData } from "./geojson_helper";
import CRS from "../geo/crs/crs";

/**
 * A source containing GeoJSON.
 * (See the [Style Specification](https://www.mapbox.com/mapbox-gl-style-spec/#sources-geojson) for detailed documentation of options.)
 *
 * @example
 * map.addSource('some id', {
 *     type: 'geojson',
 *     data: 'https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_10m_ports.geojson'
 * });
 *
 * @example
 * map.addSource('some id', {
 *    type: 'geojson',
 *    data: {
 *        "type": "FeatureCollection",
 *        "features": [{
 *            "type": "Feature",
 *            "properties": {},
 *            "geometry": {
 *                "type": "Point",
 *                "coordinates": [
 *                    -76.53063297271729,
 *                    39.18174077994108
 *                ]
 *            }
 *        }]
 *    }
 * });
 *
 * @example
 * map.getSource('some id').setData({
 *   "type": "FeatureCollection",
 *   "features": [{
 *       "type": "Feature",
 *       "properties": { "name": "Null Island" },
 *       "geometry": {
 *           "type": "Point",
 *           "coordinates": [ 0, 0 ]
 *       }
 *   }]
 * });
 * @see [Draw GeoJSON points](https://www.mapbox.com/mapbox-gl-js/example/geojson-markers/)
 * @see [Add a GeoJSON line](https://www.mapbox.com/mapbox-gl-js/example/geojson-line/)
 * @see [Create a heatmap from points](https://www.mapbox.com/mapbox-gl-js/example/heatmap/)
 * @see [Create and style clusters](https://www.mapbox.com/mapbox-gl-js/example/cluster/)
 */
// mapbox-gl-修改说明：继承于souce增强类
// 修改人：龚跃健 2024年6月17日
class GeoJSONSource extends SourceEnhance implements Source {
    type: "geojson";
    id: string;
    minzoom: number;
    maxzoom: number;
    tileSize: number;
    attribution: string;
    promoteId: ?PromoteIdSpecification;

    isTileClipped: boolean;
    reparseOverscaled: boolean;
    _data: GeoJSON | string;
    _options: any;
    workerOptions: any;
    // mapbox-gl-修改说明：新增crs属性
    // 修改人：龚跃健 2024年6月17日
    crs: CRS;
    actor: Actor;
    _loaded: boolean;
    _collectResourceTiming: boolean;
    _resourceTiming: Array<PerformanceResourceTiming>;
    _removed: boolean;

    /**
     * @private
     */
    constructor(
        id: string,
        options: GeoJSONSourceSpecification & {
            workerOptions?: any,
            collectResourceTiming: boolean,
        },
        dispatcher: Dispatcher,
        eventedParent: Evented
    ) {
        super();

        this.id = id;

        // `type` is a property rather than a constant to make it easy for 3rd
        // parties to use GeoJSONSource to build their own source types.
        this.type = "geojson";

        this.minzoom = 0;
        this.maxzoom = 18;
        this.tileSize = 512;
        this.isTileClipped = true;
        this.reparseOverscaled = true;
        this._removed = false;
        this._loaded = false;

        this.actor = dispatcher.getActor();
        // mapbox-gl-修改说明：新增crs属性
        // 修改人：龚跃健 2024年6月17日
        this.crs = eventedParent.crs;
        this.setEventedParent(eventedParent);

        this._data = options.data; // this.fixCrsData(options.data);
        this._options = extend({}, options);

        this._collectResourceTiming = options.collectResourceTiming;
        this._resourceTiming = [];

        if (options.maxzoom !== undefined) this.maxzoom = options.maxzoom;
        if (options.type) this.type = options.type;
        if (options.attribution) this.attribution = options.attribution;

        // mapbox-gl-修改说明：增加isWrapData属性。表示是否对geojson数据进行分段处理。如果为true，则会对超出范围的数据重新计算，保证其在世界范围内显示。如果为false，则不处理。
        // 修改人：赵凯 2024年5月27日
        const isWrapData = options.isWrapData;

        this.promoteId = options.promoteId;

        const scale = EXTENT / this.tileSize;

        // sent to the worker, along with `url: ...` or `data: literal geojson`,
        // so that it can load/parse/index the geojson data
        // extending with `options.workerOptions` helps to make it easy for
        // third-party sources to hack/reuse GeoJSONSource.
        // mapbox-gl-修改说明：设置传递到geojson-vt插件的参数
        // 修改人：赵凯 2023年8月11日
        this.workerOptions = extend(
            {
                crs: this.getEpsgCode(),
                source: this.id,
                cluster: options.cluster || false,
                geojsonVtOptions: {
                    buffer:
                        (options.buffer !== undefined ? options.buffer : 128) *
                        scale,
                    tolerance:
                        (options.tolerance !== undefined
                            ? options.tolerance
                            : 0.375) * scale,
                    extent: EXTENT,
                    maxZoom: this.maxzoom,
                    lineMetrics: options.lineMetrics || false,
                    generateId: options.generateId || false,
                    crs: this.getEpsgCode(),
                    // 对接geojson-vt插件需要传递当前坐标系信息
                    projectionParams: this.getProjectionParams(),
                    // mapbox-gl-修改说明：增加isWrapData属性。表示是否对geojson数据进行分段处理。如果为true，则会对超出范围的数据重新计算，保证其在世界范围内显示。如果为false，则不处理。
                    // 修改人：赵凯 2024年5月27日
                    isWrapData: !!isWrapData,
                },
                superclusterOptions: {
                    maxZoom:
                        options.clusterMaxZoom !== undefined
                            ? Math.min(options.clusterMaxZoom, this.maxzoom - 1)
                            : this.maxzoom - 1,
                    extent: EXTENT,
                    radius: (options.clusterRadius || 50) * scale,
                    log: false,
                    generateId: options.generateId || false,
                    crs: this.getEpsgCode(),
                    // mapbox-gl-修改说明：对接Supercluster插件需要传递当前坐标系信息,此消息会发送到worker中
                    // 修改人：赵凯 2025年12月03日
                    projectionParams: this.getProjectionParams(),
                },
                clusterProperties: options.clusterProperties,
            },
            options.workerOptions
        );
    }

    // mapbox-gl-修改说明：获取crs的code
    // 修改人：龚跃健 2024年6月17日
    getEpsgCode() {
        if (this.crs) {
            return this.crs.epsgCode;
        }
        return "EPSG:3857";
    }

    // mapbox-gl-修改说明：从map.js中移过来，获取坐标系参数
    // 修改人：龚跃健 2024年6月17日
    getProjectionParams() {
        const { crs } = this;
        const projection = crs.projection;
        // 获取裁图坐标系范围
        const extent = crs.getExtent();
        // 获取坐标系范围信息
        const info = crs.getCoordsInfo(extent);
        // 设置坐标系原点
        const width = info.width;
        const height = info.height;
        const originX = info.originX; // -180
        const originY = info.originY; // 90
        return {
            code: crs.epsgCode,
            def: crs.def || projection._def,
            originX,
            originY,
            width,
            height,
            bounds: Array.from(extent),
        };
    }

    fixCrsData(data) {
        if (this.getEpsgCode() !== CRS.EPSG3857.getEpsgCode()) {
            // console.log('before loop', data);
            data = loopData(data);
            // console.log('after loop', data);
        }
        return data;
    }

    load() {
        this.fire(new Event("dataloading", { dataType: "source" }));
        this._updateWorkerData((err) => {
            if (err) {
                this.fire(new ErrorEvent(err));
                return;
            }

            const data: Object = {
                dataType: "source",
                sourceDataType: "metadata",
            };
            if (
                this._collectResourceTiming &&
                this._resourceTiming &&
                this._resourceTiming.length > 0
            ) {
                data.resourceTiming = this._resourceTiming;
                this._resourceTiming = [];
            }

            // although GeoJSON sources contain no metadata, we fire this event to let the SourceCache
            // know its ok to start requesting tiles.
            this.fire(new Event("data", data));
        });
    }

    onAdd(map: Map) {
        this.map = map;
        this.load();
        // mapbox-gl-修改说明：支持geojson设置裁剪区
        // 修改人：赵凯 2024年7月31日
        // 初始化裁剪区
        const options = this._options || this.options;
        if (options) {
            this.setClippingArea(options.clippingArea);
        }
    }

    /**
     * Sets the GeoJSON data and re-renders the map.
     *
     * @param {Object|string} data A GeoJSON data object or a URL to one. The latter is preferable in the case of large GeoJSON files.
     * @returns {GeoJSONSource} this
     */
    setData(data: GeoJSON | string) {
        this._data = data;
        this.fire(new Event("dataloading", { dataType: "source" }));
        this._updateWorkerData((err) => {
            if (err) {
                this.fire(new ErrorEvent(err));
                return;
            }

            const data: Object = {
                dataType: "source",
                sourceDataType: "content",
            };
            if (
                this._collectResourceTiming &&
                this._resourceTiming &&
                this._resourceTiming.length > 0
            ) {
                data.resourceTiming = this._resourceTiming;
                this._resourceTiming = [];
            }
            this.fire(new Event("data", data));
        });

        return this;
    }

    /**
     * For clustered sources, fetches the zoom at which the given cluster expands.
     *
     * @param clusterId The value of the cluster's `cluster_id` property.
     * @param callback A callback to be called when the zoom value is retrieved (`(error, zoom) => { ... }`).
     * @returns {GeoJSONSource} this
     */
    getClusterExpansionZoom(clusterId: number, callback: Callback<number>) {
        this.actor.send(
            "geojson.getClusterExpansionZoom",
            { clusterId, source: this.id },
            callback
        );
        return this;
    }

    /**
     * For clustered sources, fetches the children of the given cluster on the next zoom level (as an array of GeoJSON features).
     *
     * @param clusterId The value of the cluster's `cluster_id` property.
     * @param callback A callback to be called when the features are retrieved (`(error, features) => { ... }`).
     * @returns {GeoJSONSource} this
     */
    getClusterChildren(
        clusterId: number,
        callback: Callback<Array<GeoJSONFeature>>
    ) {
        this.actor.send(
            "geojson.getClusterChildren",
            { clusterId, source: this.id },
            callback
        );
        return this;
    }

    /**
     * For clustered sources, fetches the original points that belong to the cluster (as an array of GeoJSON features).
     *
     * @param clusterId The value of the cluster's `cluster_id` property.
     * @param limit The maximum number of features to return.
     * @param offset The number of features to skip (e.g. for pagination).
     * @param callback A callback to be called when the features are retrieved (`(error, features) => { ... }`).
     * @returns {GeoJSONSource} this
     */
    getClusterLeaves(
        clusterId: number,
        limit: number,
        offset: number,
        callback: Callback<Array<GeoJSONFeature>>
    ) {
        this.actor.send(
            "geojson.getClusterLeaves",
            {
                source: this.id,
                clusterId,
                limit,
                offset,
            },
            callback
        );
        return this;
    }

    /*
     * Responsible for invoking WorkerSource's geojson.loadData target, which
     * handles loading the geojson data and preparing to serve it up as tiles,
     * using geojson-vt or supercluster as appropriate.
     */
    _updateWorkerData(callback: Callback<void>) {
        this._loaded = false;
        const options = extend({}, this.workerOptions);
        let data = this._data;

        if (typeof data === "string") {
            options.request = this.map._requestManager.transformRequest(
                browser.resolveURL(data),
                ResourceType.Source
            );
            options.request.collectResourceTiming = this._collectResourceTiming;
        } else {
            options.data = JSON.stringify(data);
        }
        // target {this.type}.loadData rather than literally geojson.loadData,
        // so that other geojson-like source types can easily reuse this
        // implementation
        this.actor.send(`${this.type}.loadData`, options, (err, result) => {
            if (this._removed || (result && result.abandoned)) {
                return;
            }

            this._loaded = true;

            if (
                result &&
                result.resourceTiming &&
                result.resourceTiming[this.id]
            )
                this._resourceTiming = result.resourceTiming[this.id].slice(0);
            // Any `loadData` calls that piled up while we were processing
            // this one will get coalesced into a single call when this
            // 'coalesce' message is processed.
            // We would self-send from the worker if we had access to its
            // message queue. Waiting instead for the 'coalesce' to round-trip
            // through the foreground just means we're throttling the worker
            // to run at a little less than full-throttle.
            this.actor.send(
                `${this.type}.coalesce`,
                { source: options.source },
                null
            );
            callback(err);
        });
    }

    loaded(): boolean {
        return this._loaded;
    }

    loadTile(tile: Tile, callback: Callback<void>) {
        const message = !tile.actor ? "loadTile" : "reloadTile";
        tile.actor = this.actor;
        const params = {
            type: this.type,
            uid: tile.uid,
            tileID: tile.tileID,
            zoom: tile.tileID.overscaledZ,
            maxZoom: this.maxzoom,
            tileSize: this.tileSize,
            source: this.id,
            pixelRatio: browser.devicePixelRatio,
            showCollisionBoxes: this.map.showCollisionBoxes,
            promoteId: this.promoteId,
        };

        tile.request = this.actor.send(message, params, (err, data) => {
            delete tile.request;
            tile.unloadVectorData();

            if (tile.aborted) {
                return callback(null);
            }

            if (err) {
                return callback(err);
            }

            tile.loadVectorData(
                data,
                this.map.painter,
                message === "reloadTile"
            );

            return callback(null);
        });
    }

    abortTile(tile: Tile) {
        if (tile.request) {
            tile.request.cancel();
            delete tile.request;
        }
        tile.aborted = true;
    }

    unloadTile(tile: Tile) {
        tile.unloadVectorData();
        this.actor.send("removeTile", {
            uid: tile.uid,
            type: this.type,
            source: this.id,
        });
    }

    onRemove() {
        this._removed = true;
        this.actor.send("removeSource", { type: this.type, source: this.id });
    }
    /**
     * fix: GeoJSONSource序列化时，语法验证crs参数报错
     * 修改说明: GeoJSONSource序列化时不导出crs对象，GeoJSONSource上属性验证改为弱验证
     * 版权所有: 武汉中地数码科技有限公司
     * 修改人: 赵凯 2024-12-13
     */
    serialize() {
        const geojsonSourceObject = extend({}, this._options, {
            type: this.type,
            data: this._data,
        });
        delete geojsonSourceObject.crs;
        return geojsonSourceObject;
    }

    hasTransition() {
        return false;
    }
}

export default GeoJSONSource;
